// ============================================================================
// STATISTIQUES › STOCK › STOCK PAR TAILLES — la grille de tailles en colonnes, les niveaux choisis en lignes : on voit d'un
// coup d'œil quelle taille manque et laquelle dort. Réf. maquette du back-office (écran `sgt`, « Statistiques · Stock par
// tailles ») et docs/CADRAGE-Statistiques-Stock.md §11.
//
// ⚠️ L'ÉCRAN NOMME, CHOISIT ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : la grille par défaut (la plus stockée), ses colonnes,
//    chaque cellule et ce qui est hors des colonnes viennent du serveur (`sales/stats-stock-sizes.ts`).
// ⚠️ UNE TAILLE JAMAIS CRÉÉE S'ÉCRIT « — », UNE TAILLE ÉPUISÉE « 0 » (en rose) : « le fournisseur ne la fait pas » n'est pas
//    « il n'en reste plus ».
// ⚠️ LES MESURES SE COCHENT (au moins une) et s'empilent dans la même cellule, dans l'ordre du dessin ; la première décide
//    du tri. Les seuils de couleur du taux de sortie sont SERVIS.
// ============================================================================
import { useState, type ReactNode } from 'react';
import { Icon, Trans } from '@orgazone/ui-web';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type { SalesSummaryFilterKey, StockSizeCell, StockSizesView } from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { exporterCsv } from './export-csv';
import { FILTER_DIMS, POP_HEAD, StatsPopover, dimLabel } from './stats-bar';
import { csvQty, csvRate, eur0, pctOf, qty, shownValue } from './stats-format';
import { StatsGrid, sortStatsRows, type GridCell, type GridColumn, type StatsSort } from './stats-grid';

/** Une mesure qu'une cellule peut porter. */
export type SizeMeasure = 'stock' | 'value' | 'rate' | 'sold';

/** Les mesures cochées à l'ouverture — celles du dessin. */
export const DEFAULT_SIZE_MEASURES: SizeMeasure[] = ['stock', 'value', 'rate'];

/** Le tri d'ouverture : l'ordre des niveaux, à égalité le plus gros total de la première mesure. */
export const STOCK_SIZES_DEFAULT_SORT: StatsSort = { k: 'all', d: -1, man: false };

const ALL_KEY = 'all';

const MEASURES: { k: SizeMeasure; label: Translatable; desc: Translatable }[] = [
  { k: 'stock', label: key('Qté stock'), desc: key('Quantité en stock') },
  { k: 'value', label: key('Valeur {symbole} HT'), desc: key('Stock valorisé au PMP de la date') },
  { k: 'rate', label: key('Taux de sortie'), desc: key('Vendu / (vendu + stock)') },
  { k: 'sold', label: key('Qté vendue'), desc: key('Pièces vendues sur la période') },
];
/** Les mesures que l'onglet connaît — une vue enregistrée n'en reprend pas d'autre. */
export const SIZE_MEASURES: readonly SizeMeasure[] = MEASURES.map((m) => m.k);

const measureLabel = (m: { label: Translatable }): string => t(m.label, { symbole: currentCurrency().symbol });

/** Les mesures cochées, dans l'ordre du dessin. */
const ordered = (on: readonly SizeMeasure[]) => MEASURES.filter((m) => on.includes(m.k));

// ─── Les réglages de l'onglet ────────────────────────────────────────────────────────────────────

/** Une pastille de réglage du dessin : un point, un libellé, un chevron. */
function Pill({ children, title, onOpen, dot }: { children: ReactNode; title: string; onOpen: (r: DOMRect) => void; dot?: boolean }) {
  return (
    <button type="button" title={title} aria-haspopup="dialog" onClick={(e) => onOpen(e.currentTarget.getBoundingClientRect())} style={{
      height: 30, padding: '0 11px', border: '1.5px solid var(--teal-400)', borderRadius: 'var(--r-pill)', background: 'var(--paper)',
      color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 7,
      whiteSpace: 'nowrap', cursor: 'pointer', boxSizing: 'border-box',
    }}>
      {dot && <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--teal-400)', flexShrink: 0 }} />}
      {children}
      <Icon name="chevronD" size={12} stroke={2.4} />
    </button>
  );
}

/** Une ligne d'un menu : son libellé, sa précision, coché ou non. */
function MenuRow({ label, desc, on, onPick, dot }: { label: string; desc: string; on: boolean; onPick: () => void; dot?: string }) {
  return (
    <button type="button" aria-pressed={on} onClick={onPick} className="oz-stats-menu-row" style={{
      display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 9px', border: 'none', borderRadius: 'var(--r-xs)',
      background: on ? (dot ? 'var(--teal-100)' : 'var(--ink-50)') : 'transparent', textAlign: 'left', cursor: 'pointer',
    }}>
      {dot && <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: dot, flexShrink: 0 }} />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: on && dot ? 'var(--teal-800)' : 'var(--ink-900)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>{desc}</span>
      </span>
      {on && <Icon name="check" size={13} stroke={3} color="var(--teal-600)" style={{ flexShrink: 0 }} />}
    </button>
  );
}

/**
 * Au bout de la ligne PÉRIODE : les mesures empilées (au moins une) et la grille lue — une seule à la fois, la plus stockée
 * par défaut.
 */
export function StockSizesControls({ view, measures, onMeasures, onGrid }: {
  view: StockSizesView | null;
  measures: SizeMeasure[];
  onMeasures: (next: SizeMeasure[]) => void;
  onGrid: (grid: string) => void;
}) {
  const [menu, setMenu] = useState<{ kind: 'measures' | 'grid'; r: DOMRect } | null>(null);
  const close = () => setMenu(null);
  const shown = ordered(measures);
  const grid = view?.grids.find((g) => g.id === view.grid) ?? null;
  return (
    <>
      <Pill dot title={t('Choisir les mesures empilées dans chaque cellule')} onOpen={(r) => setMenu({ kind: 'measures', r })}>
        {shown.length === 1 ? measureLabel(shown[0]!) : t('{n} mesure', { n: shown.length })}
      </Pill>
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--ink-200)', margin: '0 2px' }} />
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
        whiteSpace: 'nowrap',
      }}>{t('Grille')}</span>
      <Pill title={t('Une seule grille à la fois : des S/M/L et des 38/40/42 ne se superposent pas')} onOpen={(r) => setMenu({ kind: 'grid', r })}>
        {grid?.name ?? t('Aucune grille')}
      </Pill>
      {menu?.kind === 'measures' && (
        <StatsPopover r={menu.r} width={286} onClose={close}>
          <div style={POP_HEAD}>{t('Mesures affichées')}</div>
          <div style={{ padding: '4px 5px 5px' }}>
            {MEASURES.map((m) => {
              const on = measures.includes(m.k);
              return (
                // ⚠️ AU MOINS UNE : décocher la dernière ne fait rien — une cellule vide ne dirait rien.
                <MenuRow key={m.k} label={measureLabel(m)} desc={t(m.desc)} on={on} dot={on ? 'var(--teal-500)' : 'var(--ink-300)'}
                  onPick={() => { if (on && measures.length === 1) return; onMeasures(on ? measures.filter((x) => x !== m.k) : [...measures, m.k]); }} />
              );
            })}
          </div>
        </StatsPopover>
      )}
      {menu?.kind === 'grid' && view && (
        <StatsPopover r={menu.r} width={268} onClose={close}>
          <div style={POP_HEAD}>{fieldLabel('sizeGrid')}</div>
          <div style={{ padding: '4px 5px 5px' }}>
            {view.grids.map((g) => (
              <MenuRow key={g.id} label={g.name} on={g.id === view.grid} onPick={() => { onGrid(g.id); close(); }}
                desc={`${g.sizes.join(' · ')} — ${t('{n} article', { n: g.models })}`} />
            ))}
          </div>
        </StatsPopover>
      )}
    </>
  );
}

// ─── Les cellules et le tri ──────────────────────────────────────────────────────────────────────

/** Le taux de sortie d'une taille, en pourcentage entier — la précision du dessin. */
const rate0 = (bp: number): string => `${nombre(pctOf(bp), { fractionDigits: 0 })} %`;

/** Une mesure d'une cellule : son texte et sa teinte. ⚠️ Une taille jamais créée se dit « — ». */
function measureLine(c: StockSizeCell, m: SizeMeasure, th: StockSizesView['thresholds']): { text: string; fg: string } {
  if (!c.made) return { text: '—', fg: 'var(--ink-300)' };
  if (m === 'stock') return { text: qty(c.stockQty), fg: c.stockQty === 0 ? 'var(--rose-fg)' : 'var(--ink-900)' };
  if (m === 'value') return { text: eur0(c.stockValueCents), fg: 'var(--ink-600)' };
  if (m === 'sold') return { text: qty(c.soldQty), fg: 'var(--ink-600)' };
  if (c.sellThroughBp === null) return { text: '—', fg: 'var(--ink-300)' };
  return {
    text: rate0(c.sellThroughBp),
    fg: c.sellThroughBp >= th.fastSellThroughBp ? 'var(--teal-800)' : c.sellThroughBp < th.slowSellThroughBp ? 'var(--amber-fg)' : 'var(--ink-700)',
  };
}

/** Une cellule : une ligne par mesure cochée, la première plus grande. */
function cell(c: StockSizeCell, measures: readonly SizeMeasure[], th: StockSizesView['thresholds']): GridCell {
  const lines = ordered(measures).map((m, i) => ({ ...measureLine(c, m.k, th), size: i === 0 ? 'var(--t-body)' : 'var(--t-meta)', fw: i === 0 ? 700 : 500 }));
  return { text: lines.map((l) => l.text).join(' '), fg: lines[0]?.fg ?? 'var(--ink-900)', fw: 700, lines };
}

type Row = StockSizesView['rows'][number];

/** La valeur d'une cellule pour la mesure qui trie — `null` pour une taille jamais créée ou une valeur inconnue. */
function sortValue(c: StockSizeCell | undefined, m: SizeMeasure): number | null {
  if (!c?.made) return null;
  return m === 'stock' ? c.stockQty : m === 'value' ? c.stockValueCents : m === 'sold' ? c.soldQty : c.sellThroughBp;
}

/** Les lignes dans l'ordre demandé — la PREMIÈRE mesure cochée décide ; à égalité, le plus gros total de cette mesure. */
export function sortStockSizeRows(view: StockSizesView, measures: readonly SizeMeasure[], sort: StatsSort, lang: string): Row[] {
  const m = ordered(measures)[0]?.k ?? 'stock';
  const at = (r: Row, k: string) => (k === ALL_KEY ? r.all : r.cells[view.sizes.findIndex((s) => `sz:${s.id}` === k)]);
  return sortStatsRows(view.rows, view.axes.length, sort, (r, k) => sortValue(at(r, k), m),
    (x, y) => (sortValue(y.all, m) ?? Number.NEGATIVE_INFINITY) - (sortValue(x.all, m) ?? Number.NEGATIVE_INFINITY), lang);
}

/** Les colonnes : une par taille, puis « Toutes tailles ». */
function columnsOf(view: StockSizesView): GridColumn[] {
  return [
    ...view.sizes.map((s): GridColumn => ({
      k: `sz:${s.id}`, label: s.label, sub: fieldLabel('size'), width: 88, track: 'minmax(88px,1fr)', color: 'var(--teal-500)', headFg: 'var(--ink-800)',
      title: t('Trier sur cette taille'),
    })),
    {
      k: ALL_KEY, label: t('Toutes tailles'), sub: t('total'), width: 118, track: '118px', color: 'var(--ink-700)', headBg: 'var(--ink-50)',
      headFg: 'var(--ink-900)', title: t('Trier sur le total de la ligne'),
    },
  ];
}

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/**
 * LE TABLEAU DU STOCK PAR TAILLES — une colonne par taille de la grille lue, puis « Toutes tailles ».
 * `filtered` dit si un filtre est posé : l'état vide ne dit pas la même chose avec ou sans.
 */
export function StockSizesTable({ view, measures, sort, onSort, filtered, onReset, lang, busy = false }: {
  view: StockSizesView;
  measures: SizeMeasure[];
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
}) {
  const rows = sortStockSizeRows(view, measures, sort, lang);
  const axisDim = (k: SalesSummaryFilterKey) => FILTER_DIMS.find((d) => d.k === k)!;
  const totalSub = [t('{n} ligne', { n: view.rows.length }), ...(sort.man ? [] : [t('ordre de la ventilation')])].join(' · ');
  const strong = (x: ReactNode) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>;

  return (
    <StatsGrid label={t('Stock par tailles')} sort={sort} onSort={onSort} busy={busy} lastAxis={null}
      axes={view.axes.map((k) => ({ label: dimLabel(axisDim(k)), code: k === 'art' }))}
      columns={columnsOf(view)}
      total={{ sub: totalSub, cells: [...view.total.cells, view.total.all].map((c) => cell(c, measures, view.thresholds)) }}
      rows={rows.map((r) => ({
        key: r.parts.map((p) => p.value).join('\u001f'), parts: r.parts, sub: t('{n} article', { n: r.articles }),
        cells: [...r.cells, r.all].map((c) => cell(c, measures, view.thresholds)),
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Ni stock ni mouvement dans cette grille sur cette période')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez une autre grille ou une autre période.')}</div>
                </>
              )}
            </div>
          )}
          {view.unsizedPieces !== 0 && (
            // ⚠️ HORS DES COLONNES, ET DIT : sans quoi le total des tailles serait plus petit que le stock des modèles.
            <div role="note" style={{
              padding: '9px 16px', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 600,
              borderTop: '1px solid var(--ink-200)',
            }}>
              {t('{n} pièce de cette grille n’a pas de taille de la grille : elle est hors des colonnes.', { n: view.unsizedPieces })}
            </div>
          )}
          <div style={{
            padding: '10px 16px', background: 'var(--ink-50)', borderRadius: '0 0 var(--r-sm) var(--r-sm)', fontSize: 'var(--t-meta)',
            color: 'var(--ink-500)', lineHeight: 1.55,
          }}>
            <Trans k='Une colonne par taille de la grille choisie — <1>seuls les articles rattachés à cette grille</1> sont comptés : des S/M/L et des 38/40/42 ne se superposent pas. Une taille <2>jamais créée</2> affiche « — », jamais 0 : « le fournisseur ne la fait pas » n’est pas « il n’en reste plus ». Le <3>taux de sortie</3> se recalcule sur les sommes — vendu / (vendu + stock) —, jamais moyenné entre tailles ; le stock est celui de la fin de la période, au PMP de la date.'
              parts={[strong, strong, strong]} />
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les niveaux, chaque taille et « Toutes tailles » pour chaque mesure cochée, le tri.
 * ⚠️ UNE TAILLE JAMAIS CRÉÉE ET UN NOMBRE INCONNU RESTENT VIDES dans le fichier — un 0 y dirait « épuisée ».
 */
export function exportStockSizes(view: StockSizesView, measures: SizeMeasure[], sort: StatsSort, lang: string): void {
  const shown = ordered(measures);
  const cols = [...view.sizes.map((s) => s.label), t('Toutes tailles')];
  const head = cols.flatMap((c) => shown.map((m) => `${c} — ${measureLabel(m)}${m.k === 'rate' ? ' (%)' : ''}`));
  const values = (cells: StockSizeCell[]): string[] => cells.flatMap((c) => shown.map((m) => {
    if (!c.made) return '';
    if (m.k === 'stock') return csvQty(c.stockQty);
    if (m.k === 'sold') return csvQty(c.soldQty);
    if (m.k === 'value') return c.stockValueCents === null ? '' : toInputValue(c.stockValueCents);
    return c.sellThroughBp === null ? '' : csvRate(c.sellThroughBp);
  }));
  const rows = sortStockSizeRows(view, measures, sort, lang);
  exporterCsv(`stock-par-tailles-${view.period.from}-${view.period.to}`,
    [...view.axes.map((k) => dimLabel(FILTER_DIMS.find((d) => d.k === k)!)), t('Articles'), ...head],
    [
      [t('TOTAL'), ...view.axes.slice(1).map(() => ''), String(view.scope.references), ...values([...view.total.cells, view.total.all])],
      ...rows.map((r) => [
        ...r.parts.map((p) => (p.label !== p.value ? `${p.value} — ${shownValue(p.label)}` : shownValue(p.value))), String(r.articles),
        ...values([...r.cells, r.all]),
      ]),
    ]);
}
